<template>
  <v-container fluid class="pa-0">
    <EntityIndex
        :loading="loading"
        :total-count="totalCount"
        :table-headers="tableHeaders"
        :table-rows="tableRows"
        :table-options.sync="tableOptions"
        :selected-rows.sync="selectedRows"
        :selected-rows-actions="selectedRowsActions"
        :no-data-text="$t('partOrder.noPartOrders')"
        item-key="partOrder.id">

      <template v-slot:actions>
        <v-btn v-if="$featurePermissions.partOrderCRUD"
               color="primary"
               elevation="2"
               class="mb-2"
               @click="$refs.partOrderDialog.openCreateNewPartOrder()">
          <v-icon>mdi-plus</v-icon>
          {{ $t('actions.add') }}
        </v-btn>
      </template>

      <template v-slot:item.modifyTime="{ item }">
        <router-link :to="item.routerLinkTo">{{ item.modifyTime }}</router-link>
      </template>
      <template v-slot:item.orderedParts="{ item }">
        <part-quantities-view v-model="item.partOrder.orderedParts"/>
      </template>

      <template v-slot:entityListItem="props">
        <part-order-list-item
            :item="props.item"
            :show-actions="true"
            :row-click-enabled="true"
        />
      </template>
    </EntityIndex>

    <entity-confirm-delete-dialog
        ref="confirmDeleteDialog"
        :text="$t('partOrder.deleteConfirm')"
        :text-plural="$t('partOrder.deleteConfirmPlural')"
        @confirmedDelete="deletePartOrders">

      <template v-slot:entityListItem="props">
        <part-order-list-item :item="props.item"/>
      </template>
    </entity-confirm-delete-dialog>

    <part-order-dialog ref="partOrderDialog" @done="fetch"/>
    <part-order-mail-sending-dialog ref="mailSendingDialog"
                                    @done="fetch"/>
  </v-container>
</template>

<style scoped>

</style>

<script>
import i18n from '@/i18n';
import EntityIndex from '@/components/entity-crud/EntityIndex';
import EntityConfirmDeleteDialog from '@/components/entity-crud/EntityDeleteConfirmDialog';
import PartOrdersService from '@/services/PartOrdersService';
import PartOrderListItem from '@/views/partorder/PartOrderListItem';
import PartOrderDialog from '@/views/partorder/PartOrderDialog';
import PartOrderMailSendingDialog from '@/views/partorder/PartOrderMailSendingDialog';
import PartQuantitiesView from '@/components/layouts/PartQuantitiesView';

export default {
  components: {
    PartQuantitiesView,
    PartOrderMailSendingDialog,
    PartOrderDialog,
    PartOrderListItem,
    EntityConfirmDeleteDialog,
    EntityIndex,
  },
  name: 'PartOrderIndex',
  data() {
    return {
      partOrders: [],
      selectedRows: [],
      loading: false,
      totalCount: 0,
    };
  },
  computed: {
    tableOptions: {
      get() {
        return this.$store.state.partOrdersViewState.tableOptions;
      },
      set(value) {
        this.$store.commit('partOrdersViewState/setTableOptions', value);
      },
    },

    tableHeaders() {
      return [
        {
          value: 'modifyTime',
          text: i18n.t('partOrder.modifyTime'),
          getValue: (partOrder) => {
            return this.$options.filters.dateTime(partOrder.modifyTime);
          },
        },
        {
          value: 'supplier',
          text: i18n.t('supplier.singular'),
          getValue: (partOrder) => {
            return this.$options.filters.supplier(partOrder.supplier);
          },
        },
        {
          value: 'orderedParts',
          text: i18n.t('partOrder.orderedParts'),
          getValue: (partOrder) => {
            return partOrder.orderedParts;
          },
        },
        {
          value: 'sendTime',
          text: i18n.t('partOrder.sendTime'),
          getValue: (partOrder) => {
            return this.$options.filters.dateTime(partOrder.sendTime);
          },
        },
        {
          value: 'rowActions',
          text: '',
          getValue: (partOrder) => {
            return this.createRowActions(partOrder);
          },
        },
      ];
    },
    tableRows() {
      return this.partOrders.map((partOrder) => {
        let row = {};
        row.partOrder = partOrder;
        row.routerLinkTo = `partorders/${partOrder.id}`;

        row.rowClick = () => {
          this.$router.push(`partorders/${partOrder.id}`);
        };

        this.tableHeaders.forEach((header) => {
          if (header.getValue) {
            row[header.value] = header.getValue(partOrder);
          }
        });
        return row;
      });
    },

    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    selectedRowsActions() {
      const actions = [
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          disabled: this.selectedRows.length === 0,
          hasPermission: this.$featurePermissions.partOrderCRUD,
          click: () => {
            this.confirmDeletePartOrders(this.selectedRows);
          },
        },
      ];
      return actions.filter((action) => action.hasPermission);
    },
  },
  methods: {
    createRowActions(partOrder) {
      let actions = [
        {
          label: i18n.t('actions.show'),
          icon: 'mdi-view-list',
          hasPermission: this.$featurePermissions.partOrderCRUD,
          click: () => {
            this.$router.push(`partorders/${partOrder.id}`);
          },
        },
        {
          label: i18n.t('partOrder.sendEmailAction'),
          icon: 'mdi-email-send-outline',
          hasPermission: this.$featurePermissions.partOrderCRUD,
          click: () => {
            this.$refs.mailSendingDialog.open(partOrder.id);
          },
        },
        {
          label: i18n.t('actions.modify'),
          icon: 'mdi-pencil-outline',
          hasPermission: this.$featurePermissions.partOrderCRUD,
          click: () => {
            this.openEditDialog(partOrder.id);
          },
        },
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          dividerBefore: true,
          hasPermission: this.$featurePermissions.partOrderCRUD,
          click: () => {
            this.confirmPartOrderDelete([{ partOrder: partOrder }]);
          },
        },
      ];
      return actions.filter((action) => action.hasPermission);
    },

    confirmDeletePartOrders(items) {
      this.$refs.confirmDeleteDialog.show(items);
    },
    deletePartOrders(items) {
      const partOrderIds = items.map((item) => item.partOrder.id);
      PartOrdersService.delete(partOrderIds).finally(() => {
        this.$refs.confirmDeleteDialog.close();
        this.fetch();
      });
    },

    openEditDialog(partOrderId) {
      this.$refs.partOrderDialog.openEditForPartOrder(partOrderId);
    },

    fetch() {
      this.loading = true;
      this.selectedRows = [];
      PartOrdersService.list({
        limit: this.tableOptions.itemsPerPage,
        offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
        orderBy: this.tableOptions.sortBy[0],
        order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC',
      })
          .then((result) => {
            this.loading = false;
            this.partOrders = result.data.items;
            this.totalCount = result.data.totalCount;
          })
          .catch((result) => {
            console.log(JSON.stringify(result));
            this.loading = false;
          });
    },
  },

  watch: {
    tableOptions: {
      handler() {
        this.fetch();
      },
      deep: true,
    },
  },
  created() {
    this.fetch();
  },
};
</script>
